/**
 * pi-nonograms - A game of nonograms
 * https://github.com/DomenicoDeFelice/Pi-Nonograms
 *
 * Copyright (c) 2013-2020 Domenico De Felice
 * Released under the GNU GPL v3 License
 *
 * @license
 * @format
 * @flow
 */

import type { Cell } from './Model.js';

const Srand = require('seeded-rand');
const DragHelper = require('./DragHelper.js');
const Event = require('./Event.js');
const Model = require('./Model.js');
const View = require('./View.js');

class Controller {
  _model: Model;
  _view: View;
  _dragHelper: DragHelper;

  constructor(model: Model, view: View) {
    this._dragHelper = new DragHelper();

    this._model = model;
    this._view = view;

    // Application Logic
    model.events.nonogramChanged.attach(view.rebuildNonogram);
    model.events.guessChanged.attach((model: Model, opts) => {
      view.setGuessAt(opts.x, opts.y, opts.newGuess);
    });

    model.events.nonogramSolved.attach(view.setSolved);
    model.events.nonogramUnsolved.attach(view.setUnsolved);

    view.events.mouseDownOnCell.attach((view: View, cell) => {
      this._dragHelper.start(
        cell.x,
        cell.y,
        this._nextGuess(model.getGuessAt(cell.x, cell.y))
      );
      this._previewDragging();
    });

    view.events.mouseUp.attach(() => {
      if (!this._dragHelper.isDragging()) {
        return;
      }

      this._dragHelper.stop();
      this._cancelDraggingPreview();
      this._applyDragging();
    });

    view.events.mouseEntersCell.attach((view: View, cell) => {
      view.highlightColumn(cell.x);
      if (!this._dragHelper.isDragging()) {
        return;
      }

      this._cancelDraggingPreview();
      this._dragHelper.to(cell.x, cell.y);
      this._previewDragging();
    });

    view.events.mouseLeavesCell.attach((view: View, cell) => {
      view.unhighlightColumn(cell.x);
    });
  }

  // Private methods

  // Cycle in [unknown, filled, empty].
  _nextGuess(guess: Cell): Cell {
    const choices: Array<Cell> = ['unknown', 'filled', 'empty'];
    return choices[(choices.indexOf(guess) + 1) % choices.length];
  }

  _previewDragging = (): void => {
    this._dragHelper.iterateOverDraggedCells((x, y, guess) => {
      this._view.setGuessAt(x, y, guess);
    });
  };

  _applyDragging = () => {
    this._dragHelper.iterateOverDraggedCells((x, y, guess) => {
      this._model.setGuessAt(x, y, guess);
    });
  };

  _cancelDraggingPreview = () => {
    this._dragHelper.iterateOverDraggedCells((x, y, guess) => {
      this._view.setGuessAt(x, y, this._model.getGuessAt(x, y));
    });
  };
}

module.exports = Controller;
